import type { ReactNode } from 'react';
import { useViewport } from './useViewport';

export interface PhoneOnlyGateProps {
  children: ReactNode;
  title?: string;
  message?: string;
  /** Optional back / home control */
  action?: ReactNode;
}

/**
 * Renders children only at tablet width and above (≥768px).
 * On phone, shows a centered gate card (for visual builders and desktop-only tools).
 */
export default function PhoneOnlyGate({
  children,
  title = 'Tablet or larger screen required',
  message = 'Open on a tablet or computer to use this tool.',
  action,
}: PhoneOnlyGateProps): React.ReactElement {
  const { isPhone } = useViewport();

  if (!isPhone) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-[50vh] items-center justify-center p-6">
      <div className="max-w-md w-full rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        <p className="mt-3 text-sm text-gray-600 dark:text-slate-400">{message}</p>
        {action && <div className="mt-6 flex justify-center">{action}</div>}
      </div>
    </div>
  );
}
